<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useResumeStore } from "~/stores/useResumeStore";
import { useI18n } from "~/composables/useI18n";
import EducationList from "../EducationList/EducationList.vue";
import LearningCard from "../LearningCard/index.vue";

const t = useI18n();

const store = useResumeStore();
const { education, courses, books, professionalInterests } = storeToRefs(store);

const sortedCourses = computed(() =>
  [...courses.value].sort((a, b) => {
    if (!a.endDate && !b.endDate) {
      return 0;
    }
    if (!a.endDate) {
      return -1;
    }
    if (!b.endDate) {
      return 1;
    }
    return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
  })
);

const sortedBooks = computed(() =>
  [...books.value].sort((a, b) => {
    if (!a.endDate && !b.endDate) {
      return 0;
    }
    if (!a.endDate) {
      return -1;
    }
    if (!b.endDate) {
      return 1;
    }
    return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
  })
);

const sortedInterests = computed(() =>
  [...professionalInterests.value].sort((a, b) => b.relevance - a.relevance)
);

const hasEducation = computed(() => education.value.length > 0);
const hasCourses = computed(() => sortedCourses.value.length > 0);
const hasBooks = computed(() => sortedBooks.value.length > 0);
const hasInterests = computed(() => professionalInterests.value.length > 0);

const openUrl = (url: string) => {
  window.open(url, "_blank", "noopener,noreferrer");
};
</script>

<template>
  <section
    id="learning"
    class="scroll-mt-[calc(var(--main-nav-height)+3rem)]"
    aria-label="Learning background"
  >
    <UiTitle level="2" class="text-2xl font-semibold text-left mb-4">
      {{ t("skillsPage.learningSectionHeading") }}
    </UiTitle>
    <UiParagraph class="mb-8">
      {{ t("skillsPage.learningSectionSubheading") }}
    </UiParagraph>

    <!-- Education -->
    <div v-if="hasEducation" class="mb-10">
      <UiTitle level="3" class="text-lg font-semibold text-left mb-4">
        {{ t("learningPage.educationHeading") }}
      </UiTitle>
      <EducationList :items="education" />
    </div>

    <!-- Courses -->
    <div v-if="hasCourses" class="mb-10">
      <UiTitle level="3" class="text-lg font-semibold text-left mb-4">
        {{ t("learningPage.coursesHeading") }}
      </UiTitle>
      <ul class="space-y-3">
        <li v-for="course in sortedCourses" :key="course.degreeName">
          <LearningCard
            :title="course.degreeName"
            :subtitle="course.school.name"
            :description="course.description"
            :status="course.endDate ? undefined : 'ongoing'"
            @click="course.school.url ? openUrl(course.school.url) : undefined"
          />
        </li>
      </ul>
    </div>

    <!-- Books -->
    <div v-if="hasBooks" class="mb-10">
      <UiTitle level="3" class="text-lg font-semibold text-left mb-4">
        {{ t("learningPage.booksHeading") }}
      </UiTitle>
      <ul class="space-y-3">
        <li v-for="book in sortedBooks" :key="book.title">
          <LearningCard
            :title="book.title"
            :subtitle="book.author ? `by ${book.author}` : undefined"
            :description="book.notes"
            :status="book.endDate ? undefined : 'ongoing'"
          />
        </li>
      </ul>
    </div>

    <!-- Professional Interests -->
    <div v-if="hasInterests" class="mb-6">
      <UiTitle level="3" class="text-lg font-semibold text-left mb-4">
        {{ t("learningPage.interestsHeading") }}
      </UiTitle>
      <ul class="grid lg:grid-cols-2 gap-3">
        <li
          v-for="interest in sortedInterests"
          :key="interest.topic"
          class="flex [&>*]:w-full"
        >
          <LearningCard
            :title="interest.topic"
            :description="interest.description"
          />
        </li>
      </ul>
    </div>
  </section>
</template>
